import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Search, Download, RefreshCw, Filter, X, Calendar,
  ArrowLeftRight, Package, ChevronDown, ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const STATUS_BADGE = {
  created: 'bg-stone-100 text-stone-800',
  requested: 'bg-amber-100 text-amber-800',
  shipped: 'bg-blue-100 text-blue-800',
  received: 'bg-emerald-100 text-emerald-800',
  bundle_receipt_completed: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-rose-100 text-rose-800',
  cancelled: 'bg-stone-100 text-stone-600',
};

const TYPE_BADGE = {
  STANDARD: 'bg-indigo-100 text-indigo-800',
  REWORK: 'bg-orange-100 text-orange-800',
};

/**
 * Unified Internal Transfer Report.
 *
 * Two modes:
 *  - Global Admin: pass nothing → shows all transfers with destination/source filters.
 *  - Section-Scoped: pass `sectionName` → scopes to that section (incoming + outgoing).
 *
 * Designed for both `/reports/internal-transfers` and per-section dashboards.
 */
const InternalTransferReport = ({
  sectionName = null,
  defaultDirection = 'both',
  backRoute = '/',
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // URL params can override props (when this page is reached via router link)
  const urlSection = searchParams.get('section') || sectionName;
  const urlDirection = searchParams.get('direction') || defaultDirection;

  const isSectionScoped = Boolean(urlSection);

  const today = new Date();
  const monthAgo = new Date();
  monthAgo.setDate(monthAgo.getDate() - 30);
  const fmt = (d) => d.toISOString().slice(0, 10);

  const [filters, setFilters] = useState({
    date_from: fmt(monthAgo),
    date_to: fmt(today),
    to_section: '',
    from_section: '',
    transfer_type: '',
    status: '',
    search: '',
  });
  const [direction, setDirection] = useState(urlDirection);
  const [showFilters, setShowFilters] = useState(true);
  const [transfers, setTransfers] = useState([]);
  const [summary, setSummary] = useState({ total_transfers: 0, total_bundles: 0, by_status: {}, by_type: {} });
  const [destinations, setDestinations] = useState([]);
  const [sources, setSources] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());

  const buildQS = useCallback(() => {
    const p = new URLSearchParams();
    if (filters.date_from) p.append('date_from', filters.date_from);
    if (filters.date_to) p.append('date_to', filters.date_to);
    if (filters.to_section) p.append('to_section', filters.to_section);
    if (filters.from_section) p.append('from_section', filters.from_section);
    if (filters.transfer_type) p.append('transfer_type', filters.transfer_type);
    if (filters.status) p.append('status', filters.status);
    if (filters.search) p.append('search', filters.search);
    if (urlSection) {
      p.append('section', urlSection);
      p.append('direction', direction);
    }
    p.append('limit', '1000');
    return p;
  }, [filters, urlSection, direction]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await apiClient.get(`/reports/internal-transfers?${buildQS().toString()}`);
      setTransfers(res.data.transfers || []);
      setSummary(res.data.summary || { total_transfers: 0, total_bundles: 0, by_status: {}, by_type: {} });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
    } finally {
      setIsLoading(false);
    }
  }, [buildQS]);

  useEffect(() => {
    apiClient
      .get('/reports/internal-transfers/destinations')
      .then((r) => {
        setDestinations(r.data.to_sections || []);
        setSources(r.data.from_sections || []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => { fetchData(); }, []); // eslint-disable-line

  const handleClear = () => {
    setFilters({
      date_from: fmt(monthAgo), date_to: fmt(today),
      to_section: '', from_section: '', transfer_type: '', status: '', search: '',
    });
  };

  const handleExport = (grain) => {
    if (transfers.length === 0) {
      toast.error('No data to export');
      return;
    }
    const qs = buildQS();
    qs.set('grain', grain);
    qs.delete('limit');
    const apiBase = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
    const url = `${apiBase}/reports/internal-transfers/export?${qs.toString()}`;
    const tok = localStorage.getItem('session_token');
    fetch(url, { headers: { Authorization: `Bearer ${tok}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `internal_transfer_report_${grain}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success(`Exported ${grain === 'transfer' ? 'one row per transfer' : 'one row per bundle'}`);
      })
      .catch(() => toast.error('Export failed'));
  };

  const toggleExpand = (id) => {
    const n = new Set(expanded);
    n.has(id) ? n.delete(id) : n.add(id);
    setExpanded(n);
  };

  const summaryCells = useMemo(() => ([
    { label: 'Total Transfers', value: summary.total_transfers, color: 'text-indigo-600' },
    { label: 'Total Bundles', value: summary.total_bundles, color: 'text-emerald-600' },
    { label: 'Received', value: (summary.by_status?.received || 0) + (summary.by_status?.bundle_receipt_completed || 0), color: 'text-emerald-700' },
    { label: 'In Transit', value: summary.by_status?.shipped || 0, color: 'text-blue-700' },
    { label: 'Pending', value: (summary.by_status?.created || 0) + (summary.by_status?.requested || 0), color: 'text-amber-700' },
    { label: 'Rework', value: summary.by_type?.REWORK || 0, color: 'text-orange-700' },
  ]), [summary]);

  return (
    <div className="min-h-screen bg-stone-50 p-6" data-testid="itr-report-root">
      <div className="max-w-[1400px] mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(backRoute)} data-testid="itr-back-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-semibold flex items-center gap-2">
                <ArrowLeftRight className="h-6 w-6 text-indigo-600" />
                Internal Transfer Report
                {isSectionScoped && (
                  <Badge className="bg-indigo-100 text-indigo-800 border-0">{urlSection}</Badge>
                )}
              </h1>
              <p className="text-sm text-stone-500">
                {isSectionScoped
                  ? `Transfers ${direction === 'incoming' ? 'into' : direction === 'outgoing' ? 'out of' : 'in/out of'} ${urlSection}`
                  : 'All internal transfers across every module'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowFilters((s) => !s)} data-testid="itr-toggle-filters">
              <Filter className="h-4 w-4 mr-1" /> Filters
            </Button>
            <Button variant="outline" size="sm" onClick={fetchData} data-testid="itr-refresh-btn">
              <RefreshCw className={`h-4 w-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="itr-export-btn">
                  <Download className="h-4 w-4 mr-1" /> Export CSV
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport('transfer')} data-testid="itr-export-transfer">
                  One row per transfer
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('bundle')} data-testid="itr-export-bundle">
                  One row per bundle
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Summary strip */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {summaryCells.map((c) => (
            <Card key={c.label} className="border-stone-200">
              <CardContent className="p-3">
                <div className="text-xs uppercase tracking-wider text-stone-500">{c.label}</div>
                <div className={`text-2xl font-semibold ${c.color}`} data-testid={`itr-summary-${c.label.toLowerCase().replace(/ /g, '-')}`}>
                  {c.value}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        {showFilters && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Filter className="h-4 w-4" /> Filters
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className="text-xs text-stone-600">From Date</label>
                <Input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                  data-testid="itr-filter-date-from"
                />
              </div>
              <div>
                <label className="text-xs text-stone-600">To Date</label>
                <Input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                  data-testid="itr-filter-date-to"
                />
              </div>
              {!isSectionScoped && (
                <>
                  <div>
                    <label className="text-xs text-stone-600">Destination Section</label>
                    <Select
                      value={filters.to_section || 'all'}
                      onValueChange={(v) => setFilters({ ...filters, to_section: v === 'all' ? '' : v })}
                    >
                      <SelectTrigger data-testid="itr-filter-to-section"><SelectValue placeholder="All" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Destinations</SelectItem>
                        {destinations.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-stone-600">Source Section</label>
                    <Select
                      value={filters.from_section || 'all'}
                      onValueChange={(v) => setFilters({ ...filters, from_section: v === 'all' ? '' : v })}
                    >
                      <SelectTrigger data-testid="itr-filter-from-section"><SelectValue placeholder="All" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Sources</SelectItem>
                        {sources.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              {isSectionScoped && (
                <div>
                  <label className="text-xs text-stone-600">Direction</label>
                  <Select value={direction} onValueChange={setDirection}>
                    <SelectTrigger data-testid="itr-filter-direction"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="both">Both (in & out)</SelectItem>
                      <SelectItem value="incoming">Incoming only</SelectItem>
                      <SelectItem value="outgoing">Outgoing only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <label className="text-xs text-stone-600">Type</label>
                <Select
                  value={filters.transfer_type || 'all'}
                  onValueChange={(v) => setFilters({ ...filters, transfer_type: v === 'all' ? '' : v })}
                >
                  <SelectTrigger data-testid="itr-filter-type"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="STANDARD">Standard</SelectItem>
                    <SelectItem value="REWORK">Rework</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-stone-600">Status</label>
                <Select
                  value={filters.status || 'all'}
                  onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}
                >
                  <SelectTrigger data-testid="itr-filter-status"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {['created', 'requested', 'shipped', 'received', 'bundle_receipt_completed', 'rejected', 'cancelled']
                      .map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-stone-600">Search (Transfer # / Bundle #)</label>
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-2 top-2.5 text-stone-400" />
                  <Input
                    className="pl-7"
                    placeholder="INT000473 or BUN_..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    onKeyDown={(e) => e.key === 'Enter' && fetchData()}
                    data-testid="itr-filter-search"
                  />
                </div>
              </div>
              <div className="flex items-end gap-2 md:col-span-4">
                <Button onClick={fetchData} className="bg-indigo-600 hover:bg-indigo-700" data-testid="itr-apply-filters-btn">
                  <Search className="h-4 w-4 mr-1" /> Apply
                </Button>
                <Button variant="outline" onClick={handleClear} data-testid="itr-clear-filters-btn">
                  <X className="h-4 w-4 mr-1" /> Clear
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Results */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-12 text-center text-stone-500">Loading…</div>
            ) : transfers.length === 0 ? (
              <div className="p-12 text-center text-stone-500" data-testid="itr-empty-state">
                <Calendar className="h-10 w-10 mx-auto mb-2 text-stone-300" />
                No transfers in this date range / filter combination.
              </div>
            ) : (
              <Table data-testid="itr-results-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>Transfer #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>From → To</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Bundles</TableHead>
                    <TableHead>Created By</TableHead>
                    <TableHead>Received By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transfers.map((t) => {
                    const isOpen = expanded.has(t.transfer_id);
                    return (
                      <React.Fragment key={t.transfer_id}>
                        <TableRow
                          className="cursor-pointer hover:bg-stone-50"
                          onClick={() => toggleExpand(t.transfer_id)}
                          data-testid={`itr-row-${t.transfer_id}`}
                        >
                          <TableCell>
                            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </TableCell>
                          <TableCell className="font-mono text-xs">{t.transfer_id}</TableCell>
                          <TableCell className="text-xs">{t.created_at?.slice(0, 10)}</TableCell>
                          <TableCell className="text-xs">
                            <span className="text-stone-600">{t.from_section}</span>
                            <span className="mx-1 text-stone-400">→</span>
                            <span className="font-medium">{t.to_section}</span>
                          </TableCell>
                          <TableCell>
                            <Badge className={`${TYPE_BADGE[t.transfer_type] || 'bg-stone-100'} border-0 text-[10px]`}>
                              {t.transfer_type}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={`${STATUS_BADGE[t.status] || 'bg-stone-100'} border-0 text-[10px]`}>
                              {t.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-medium">{t.bundle_count}</TableCell>
                          <TableCell className="text-xs">{t.created_by_name || '—'}</TableCell>
                          <TableCell className="text-xs">{t.received_by || '—'}</TableCell>
                        </TableRow>
                        {isOpen && (
                          <TableRow className="bg-stone-50">
                            <TableCell colSpan={9} className="text-xs">
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-2">
                                <div><span className="text-stone-500">Remarks:</span> {t.remarks || '—'}</div>
                                <div><span className="text-stone-500">Reason:</span> {t.transfer_reason || '—'}</div>
                                {t.rework_reason && <div><span className="text-stone-500">Rework reason:</span> {t.rework_reason}</div>}
                                <div><span className="text-stone-500">Staff:</span> {t.staff_name || t.staff_id || '—'}</div>
                                <div><span className="text-stone-500">Shipped at:</span> {t.shipped_at?.slice(0, 19).replace('T', ' ') || '—'}</div>
                                <div><span className="text-stone-500">Received at:</span> {t.received_at?.slice(0, 19).replace('T', ' ') || '—'}</div>
                              </div>
                              <div className="font-medium mb-1 flex items-center gap-1"><Package className="h-3 w-3" /> Bundles ({t.bundle_count})</div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-1">
                                {(t.bundles && t.bundles.length ? t.bundles : (t.bundle_codes || []).map((bc) => ({ bundle_code: bc }))).map((b, i) => (
                                  <div
                                    key={`${t.transfer_id}-${b.bundle_code}-${i}`}
                                    className="bg-white border border-stone-200 rounded px-2 py-1 text-[11px] font-mono"
                                  >
                                    {b.bundle_code}
                                    {b.design_name && <div className="text-[10px] text-stone-500 font-sans">{b.design_name}{b.shade ? ` · ${b.shade}` : ''}{b.size ? ` · ${b.size}` : ''}</div>}
                                  </div>
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InternalTransferReport;
